// @flow

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';

import RecordButton from './RecordButton';
import StopButton from './StopButton';
import Timer from './Timer';
import Countdown from './CountDown';
import BackgroundSelector from './BackgroundSelector';
import FaceGuide from './FaceGuide';
import SizeCalculator from './SizeCalculator';
import StandardButton from '../Buttons/Standard';
import useVideoCompositor from './useVideoCompositor';

import styles from './styles.module.scss';

import type { Background } from './BackgroundSelector';

const COUNTDOWN_MS = 3000;

type RecorderState = 'initializing' | 'ready' | 'countdown' | 'recording' | 'replaying' | 'error';

type Props = {
  onSendVideo: () => void,
  onChange: (data: Object) => void,
  videoBackTimeLimitSeconds: number,
  hidden: boolean,
  videoHeight?: number,
  virtualBackgroundEnabled?: boolean,
  savedBackground?: Object | null,
  onBackgroundChange?: (background: Object) => void,
  onRecordingStateChange?: (recordingState: 'STARTED' | 'DONE') => void,
};

export const VideoBackRecorder = ({
  videoBackTimeLimitSeconds,
  hidden,
  onSendVideo,
  onChange,
  videoHeight = 500,
  virtualBackgroundEnabled = false,
  savedBackground = null,
  onBackgroundChange = () => {},
  onRecordingStateChange = () => {},
}: Props) => {
  const { formatMessage } = useIntl();

  const [recorderState, setRecorderState] = useState<RecorderState>('initializing');
  const [background, setBackground] = useState<Background>(savedBackground || { type: 'none' });
  const [width, setWidth] = useState<number | null>(null);
  const [height, setHeight] = useState<number | null>(null);

  const replayVideoRef = useRef<HTMLVideoElement | null>(null);
  const recorderRef = useRef<any>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number | null>(null);
  const recordingStartTimeRef = useRef<number>(0);
  const timeLimitTimerRef = useRef<TimeoutID | null>(null);
  const countdownTimerRef = useRef<TimeoutID | null>(null);
  const thumbnailBlobRef = useRef<Blob | null>(null);
  // Stored before the state change so the useEffect below can wire it up once
  // the replay <video> has mounted — replayVideoRef.current is null at onstop time.
  const replayBlobUrlRef = useRef<string | null>(null);

  // useCallback with [] prevents re-initializing camera/MediaPipe on every render.
  const handleReady = useCallback(() => setRecorderState('ready'), []);
  const handleError = useCallback(() => setRecorderState('error'), []);

  const { videoRef, canvasRef, streamRef } = useVideoCompositor({
    width,
    height,
    background,
    onReady: handleReady,
    onError: handleError,
  });

  useEffect(() => {
    if (recorderState === 'replaying' && replayBlobUrlRef.current && replayVideoRef.current) {
      replayVideoRef.current.src = replayBlobUrlRef.current;
      replayVideoRef.current.play();
    }
  }, [recorderState]);

  useEffect(
    () => () => {
      if (countdownTimerRef.current) clearTimeout(countdownTimerRef.current);
      if (timeLimitTimerRef.current) clearTimeout(timeLimitTimerRef.current);
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop();
      }
      if (replayBlobUrlRef.current) URL.revokeObjectURL(replayBlobUrlRef.current);
    },
    [],
  );

  const startRecording = useCallback(async () => {
    const canvas = canvasRef.current;
    const stream = streamRef.current;
    if (!canvas || !stream) return;
    thumbnailBlobRef.current = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.85);
    });

    // Build a mixed stream: canvas video track + camera audio track
    const canvasStream = canvas.captureStream(30);
    const audioTrack = stream.getAudioTracks()[0];
    if (audioTrack) canvasStream.addTrack(audioTrack);

    // $FlowFixMe[cannot-resolve-name] — MediaRecorder is a browser API not in Flow's libdefs
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=h264') ? 'video/webm;codecs=h264' : 'video/webm';

    chunksRef.current = [];
    const recorder = new MediaRecorder(canvasStream, { mimeType });

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      const videoBlob = new Blob(chunksRef.current, { type: mimeType });
      const duration = Date.now() - recordingStartTimeRef.current;

      onChange({
        videoBlob,
        startedAt: startedAtRef.current,
        thumbnailBlob: thumbnailBlobRef.current,
        duration,
      });

      replayBlobUrlRef.current = URL.createObjectURL(videoBlob);
      setRecorderState('replaying');
    };

    recorderRef.current = recorder;
    startedAtRef.current = new Date().getTime();
    recordingStartTimeRef.current = Date.now();
    recorder.start();

    setRecorderState('recording');
    onRecordingStateChange('STARTED');

    if (videoBackTimeLimitSeconds) {
      timeLimitTimerRef.current = setTimeout(() => {
        // Inline the stop to avoid a circular dependency on stopRecording
        if (recorderRef.current && recorderRef.current.state === 'recording') {
          recorderRef.current.stop();
        }
        onRecordingStateChange('DONE');
      }, videoBackTimeLimitSeconds * 1000);
    }
  }, [videoBackTimeLimitSeconds, onChange, onRecordingStateChange, canvasRef, streamRef]);

  const stopRecording = useCallback(() => {
    if (timeLimitTimerRef.current) clearTimeout(timeLimitTimerRef.current);
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
    }
    onRecordingStateChange('DONE');
  }, [onRecordingStateChange]);

  const startCountdown = useCallback(() => {
    setRecorderState('countdown');
    countdownTimerRef.current = setTimeout(startRecording, COUNTDOWN_MS);
  }, [startRecording]);

  const handleReRecord = useCallback(() => {
    if (replayBlobUrlRef.current) {
      URL.revokeObjectURL(replayBlobUrlRef.current);
      replayBlobUrlRef.current = null;
    }
    setRecorderState('ready');
  }, []);

  if (!width || !height) {
    return (
      <div className={hidden ? styles.hideVideo : styles.videoHolder} style={{ height: videoHeight }}>
        <SizeCalculator onWidthCalculated={setWidth} onHeightCalculated={setHeight} />
      </div>
    );
  }

  return (
    <div className={hidden ? styles.hideVideo : styles.videoHolder} style={{ height: videoHeight }}>
      {/* Hidden video feeds frames into the canvas compositor */}
      <video ref={videoRef} className={styles.hiddenVideo} muted playsInline />

      {/* Canvas is mirrored for preview (selfie feel); replay is not mirrored so
          the recording matches what recipients see. */}
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className={classNames(styles.videoCanvas, {
          [styles.mirrored]: recorderState !== 'replaying',
          [styles.hiddenVideo]: recorderState === 'replaying',
        })}
      />

      {recorderState === 'replaying' && (
        <div className={styles.replayWrapper}>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video ref={replayVideoRef} className={styles.replayVideo} playsInline controls />
          <div className={styles.replayActions}>
            <StandardButton variant="secondary" onClick={handleReRecord} data-testid="re-record">
              {formatMessage({ id: 'app.video-back.re-record' })}
            </StandardButton>
            <StandardButton onClick={onSendVideo} data-testid="start-replaying">
              {formatMessage({ id: 'app.video-back.next' })}
            </StandardButton>
          </div>
        </div>
      )}

      {recorderState === 'countdown' && <FaceGuide width={width} height={height} />}

      {recorderState === 'ready' && virtualBackgroundEnabled && (
        <div className={styles.backgroundSelectorWrapper}>
          <BackgroundSelector
            selected={background}
            onChange={(bg) => {
              setBackground(bg);
              onBackgroundChange(bg);
            }}
          />
        </div>
      )}

      {recorderState === 'recording' && <Timer />}

      {/* StopButton's inner .border uses position:absolute; top:2px — needs a
          bottom-anchored strip as its containing block, matching the original ActionsWrapper. */}
      {recorderState === 'recording' && (
        <div className={styles.recordingControls}>
          <StopButton onClick={stopRecording} />
        </div>
      )}

      {/* RecordButton and Countdown both use position:absolute internally and need
          a full-size positioned ancestor to resolve their coordinates against. */}
      {recorderState === 'ready' && (
        <div className={styles.absoluteFill} style={{ paddingBottom: Math.round(height * 0.08) }}>
          <RecordButton onClick={startCountdown} />
        </div>
      )}
      {recorderState === 'countdown' && <Countdown countdownTime={COUNTDOWN_MS} />}

      {(recorderState === 'initializing' || recorderState === 'error') && (
        <div className={styles.actionsOverlay}>
          {recorderState === 'initializing' && (
            <p className={styles.statusText}>{formatMessage({ id: 'app.video-back.initializing' })}</p>
          )}

          {recorderState === 'error' && (
            <p className={styles.statusText}>{formatMessage({ id: 'app.video-back.camera.error' })}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoBackRecorder;
